import React, { FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useProfile } from '@brg/auth';
import { BrgButton, ButtonVariant, useToasts } from '@brg/ui';
import { LayoutConst, useModal } from 'core';
import {
  ConsultationScheduleOperation,
  LIMIT_CONSULTATION_SIGNUP_EXCEEDED,
  generateConsultationSlots,
  useConsultationScheduleStudent,
} from 'shared/hooks/api/LMS/useConsultationScheduleStudent';

const CONNECT_EARLY_MS = 60 * 1000;

interface ConsultationLesson {
  id?: string;
  title?: string | Array<{ lang: string; name: string }>;
  start?: string;
  end?: string;
  teacherName?: string;
  teacher?: string;
  isGuestTeacher?: boolean | null;
  lessonLink?: string | null;
  location?: string | null;
  room?: string | null;
  consultationFormat?: number | string | null;
  studentsMaxLimit?: number | null;
  timeSlotPerRerson?: number | null;
  signUpSheetIds?: Record<string, string> | null;
  consultation?: {
    capacity?: number;
    timePerPerson?: number;
    teacherName?: string;
    format?: 'online' | 'offline' | number | string;
    location?: string;
    room?: string;
    unavailableSlots?: string[];
    isInvitedTeacher?: boolean | null;
    isGuestTeacher?: boolean | null;
  };
}

export interface ConsultationRegistrationModalProps {
  lesson: ConsultationLesson;
  onSuccess?: () => void;
}

const ModalWrapper = styled.div`
  width: 720px;
  max-width: calc(100vw - 32px);
  padding: 48px;
  display: flex;
  flex-direction: column;
  gap: 48px;
  border: 1px solid #e7e7e7;
  border-radius: 8px;
  background: #ffffff;

  @media (max-width: ${LayoutConst.screen.s}px) {
    max-width: calc(100vw - 16px);
    padding: 24px 16px;
    gap: 24px;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
`;

const Title = styled.h2`
  margin: 0;
  color: #151514;
  font-family: Inter;
  font-size: 28px;
  font-weight: 600;
  line-height: 34px;
  letter-spacing: -0.56px;
`;

const Description = styled.p`
  margin: 0;
  color: #151514;
  font-family: Inter;
  font-size: 17px;
  font-weight: 400;
  line-height: 22px;
  letter-spacing: -0.34px;
`;

const HeaderDescription = styled(Description)`
  margin-top: 8px;
`;

const CloseButton = styled.button`
  width: 32px;
  height: 32px;
  padding: 0;
  border: 0;
  background: transparent;
  color: #909090;
  cursor: pointer;
  font-size: 36px;
  font-weight: 300;
  line-height: 24px;

  &:focus,
  &:focus-visible {
    outline: none;
  }
`;

const Slots = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px 16px;
`;

const EmptySlots = styled.div`
  color: #909090;
  font-family: Inter;
  font-size: 15px;
  font-weight: 400;
  line-height: 20px;
`;

const SlotButton = styled.button<{ $selected: boolean; $disabled: boolean }>`
  min-width: 112px;
  padding: 5px 16px;
  border: 1px solid ${({ $disabled }) => ($disabled ? '#b6b6b5' : '#909090')};
  border-radius: 5px;
  background: ${({ $selected }) => ($selected ? '#70706f' : '#ffffff')};
  color: ${({ $selected, $disabled }) => {
    if ($selected) return '#ffffff';
    return $disabled ? '#b6b6b5' : '#151514';
  }};
  cursor: ${({ $disabled }) => ($disabled ? 'default' : 'pointer')};
  font-family: Inter;
  font-size: 15px;
  font-weight: 400;
  line-height: 20px;

  &:focus,
  &:focus-visible {
    outline: none;
  }
`;

const RecordDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 8px;
`;

const ViewContent = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
`;

const RecordDetail = styled.div`
  color: #151514;
  font-family: Inter;
  font-size: 20px;
  font-weight: 600;
  line-height: 26px;
  letter-spacing: -0.4px;
`;

const Actions = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
  width: 100%;
`;

const getLessonTitle = (lesson: ConsultationLesson, language: string) => {
  if (typeof lesson.title === 'string') return lesson.title;

  return lesson.title?.find((item) => item.lang === language.toLowerCase())?.name
    || lesson.title?.[0]?.name
    || '';
};

const findStudentRegistrationSlot = (
  lesson: ConsultationLesson,
  accountId?: string | null,
) => {
  if (!accountId || !lesson.signUpSheetIds) return null;

  const entry = Object.entries(lesson.signUpSheetIds).find(
    ([, studentId]) => String(studentId) === String(accountId),
  );

  return entry?.[0] || null;
};

const isOfflineConsultation = (lesson: ConsultationLesson) => {
  const format = lesson.consultationFormat ?? lesson.consultation?.format;
  return format === 1 || String(format).toLowerCase() === 'offline';
};

const getConsultationLocation = (lesson: ConsultationLesson) => (
  lesson.location
  || lesson.room
  || lesson.consultation?.location
  || lesson.consultation?.room
  || lesson.lessonLink
  || ''
);

const formatSlotDateTime = (
  slotStart: string,
  durationMinutes: number,
  language: string,
) => {
  const start = new Date(slotStart);
  if (Number.isNaN(start.getTime())) return '';

  const end = new Date(start.getTime() + durationMinutes * 60 * 1000);
  const locale = language === 'en' ? 'en-US' : 'ru-RU';
  const datePart = start.toLocaleDateString(locale, {
    day: 'numeric',
    month: 'long',
  });
  const startTime = start.toLocaleTimeString(locale, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
  const endTime = end.toLocaleTimeString(locale, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });

  return `${datePart}, ${startTime}-${endTime}`;
};

export const ConsultationRegistrationModal: FC<ConsultationRegistrationModalProps> = ({
  lesson,
  onSuccess,
}) => {
  const { t, i18n } = useTranslation();
  const modal = useModal();
  const toasts = useToasts();
  const profile = useProfile();
  const { scheduleStudent, isLoading } = useConsultationScheduleStudent();
  const [selectedSlot, setSelectedSlot] = useState<string | null>(null);
  const lessonStart = useMemo(() => (lesson.start ? new Date(lesson.start) : null), [lesson.start]);
  const [now, setNow] = useState(() => Date.now());
  const slotDuration = lesson.consultation?.timePerPerson || lesson.timeSlotPerRerson || 15;
  const maxSlots = lesson.consultation?.capacity ?? lesson.studentsMaxLimit ?? null;
  const studentSlot = useMemo(
    () => findStudentRegistrationSlot(lesson, profile?.accountId),
    [lesson, profile?.accountId],
  );
  const isViewMode = Boolean(studentSlot);
  const isOffline = isOfflineConsultation(lesson);
  const location = getConsultationLocation(lesson);
  const connectAt = lessonStart ? lessonStart.getTime() - CONNECT_EARLY_MS : null;

  useEffect(() => {
    if (connectAt == null) return undefined;
    const delay = connectAt - Date.now();
    if (delay <= 0) {
      setNow(Date.now());
      return undefined;
    }
    const timeoutId = window.setTimeout(() => setNow(Date.now()), delay);
    return () => window.clearTimeout(timeoutId);
  }, [connectAt]);

  const canConnect = Boolean(
    studentSlot
    && !isOffline
    && lesson.lessonLink
    && connectAt != null
    && now >= connectAt,
  );

  const unavailableSlotTimes = useMemo(() => {
    const fromSheet = Object.keys(lesson.signUpSheetIds || {})
      .map((value) => new Date(value).getTime())
      .filter((value) => !Number.isNaN(value));
    const fromConsultation = (lesson.consultation?.unavailableSlots || [])
      .map((value) => new Date(value).getTime())
      .filter((value) => !Number.isNaN(value));

    return new Set([...fromSheet, ...fromConsultation]);
  }, [lesson.consultation?.unavailableSlots, lesson.signUpSheetIds]);

  const slots = useMemo(() => generateConsultationSlots({
    start: lesson.start,
    end: lesson.end,
    durationMinutes: slotDuration,
    maxSlots,
    referenceStart: lesson.start,
  }), [lesson.end, lesson.start, maxSlots, slotDuration]);

  const dateLabel = lessonStart?.toLocaleDateString(i18n.language === 'en' ? 'en-US' : 'ru-RU', {
    day: 'numeric',
    month: 'long',
  }) || '';
  const teacher = lesson.consultation?.teacherName || lesson.teacherName || lesson.teacher || '';
  const isInvitedTeacher = Boolean(
    lesson.isGuestTeacher
    ?? lesson.consultation?.isInvitedTeacher
    ?? lesson.consultation?.isGuestTeacher
  );
  const teacherLabel = teacher
    ? t(
      isInvitedTeacher
        ? 'lms.consultation.registrationModal.invitedTeacher'
        : 'lms.consultation.registrationModal.teacher',
      { name: teacher },
    )
    : '';
  const description = [getLessonTitle(lesson, i18n.language), teacherLabel]
    .filter(Boolean)
    .join('. ');
  const slotDateTimeLabel = studentSlot
    ? formatSlotDateTime(studentSlot, slotDuration, i18n.language)
    : '';

  const handleRegister = async () => {
    if (!lesson.id || !selectedSlot || !profile?.accountId) return;

    const result = await scheduleStudent({
      lessonId: lesson.id,
      timeSlot: selectedSlot,
      studentId: profile.accountId,
      operation: ConsultationScheduleOperation.Add,
    });

    if (!result.success) {
      toasts.error(
        result.error === LIMIT_CONSULTATION_SIGNUP_EXCEEDED
          ? t('lms.consultation.registrationModal.limitExceeded')
          : t('lms.consultation.registrationModal.error'),
      );
      return;
    }

    toasts.info(t('lms.consultation.registrationModal.success'));
    onSuccess?.();
    modal.hide();
  };

  const handleCancelRegistration = async () => {
    if (!lesson.id || !studentSlot || !profile?.accountId) return;

    const result = await scheduleStudent({
      lessonId: lesson.id,
      timeSlot: studentSlot,
      studentId: profile.accountId,
      operation: ConsultationScheduleOperation.Remove,
    });

    if (!result.success) {
      toasts.error(t('lms.consultation.registrationModal.error'));
      return;
    }

    toasts.info(t('lms.consultation.registrationModal.cancelSuccess'));
    onSuccess?.();
    modal.hide();
  };

  const handleConnect = () => {
    if (!canConnect || !lesson.lessonLink) return;
    window.open(lesson.lessonLink, '_blank', 'noopener,noreferrer');
  };

  if (isViewMode) {
    return (
      <ModalWrapper>
        <Header>
          <ViewContent>
            <Title>{t('lms.consultation.registrationModal.recordTitle')}</Title>
            <RecordDetails>
              {description && <Description>{description}</Description>}
              {slotDateTimeLabel && <RecordDetail>{slotDateTimeLabel}</RecordDetail>}
              {isOffline && location && <RecordDetail>{location}</RecordDetail>}
            </RecordDetails>
          </ViewContent>
          <CloseButton type="button" onClick={() => modal.hide()} aria-label="close">×</CloseButton>
        </Header>

        <Actions>
          <BrgButton
            fullWidth
            variant={ButtonVariant.Hollow}
            disabled={isLoading}
            onClick={handleCancelRegistration}
          >
            {t('lms.consultation.registrationModal.cancelRegistration')}
          </BrgButton>
          {isOffline ? (
            <BrgButton fullWidth variant={ButtonVariant.Hollow} onClick={() => modal.hide()}>
              {t('lms.consultation.registrationModal.ok')}
            </BrgButton>
          ) : (
            <BrgButton
              fullWidth
              variant={ButtonVariant.Filled}
              disabled={!canConnect || isLoading}
              onClick={handleConnect}
            >
              {t('lms.consultation.registrationModal.connect')}
            </BrgButton>
          )}
        </Actions>
      </ModalWrapper>
    );
  }

  return (
    <ModalWrapper>
      <Header>
        <div>
          <Title>{t('lms.consultation.registrationModal.title', { date: dateLabel })}</Title>
          {description && <HeaderDescription>{description}</HeaderDescription>}
        </div>
        <CloseButton type="button" onClick={() => modal.hide()} aria-label="close">×</CloseButton>
      </Header>

      <Slots>
        {slots.length === 0 ? (
          <EmptySlots>{t('lms.consultation.registrationModal.noSlots')}</EmptySlots>
        ) : (
          slots.map((slot) => {
            const isDisabled = unavailableSlotTimes.has(slot.time);
            return (
              <SlotButton
                key={slot.id}
                type="button"
                $selected={selectedSlot === slot.id}
                $disabled={isDisabled}
                disabled={isDisabled}
                onClick={() => setSelectedSlot(slot.id)}
              >
                {slot.label}
              </SlotButton>
            );
          })
        )}
      </Slots>

      <Actions>
        <BrgButton fullWidth variant={ButtonVariant.Hollow} onClick={() => modal.hide()}>
          {t('lms.consultation.registrationModal.close')}
        </BrgButton>
        <BrgButton
          fullWidth
          variant={ButtonVariant.Filled}
          disabled={!selectedSlot || isLoading}
          onClick={handleRegister}
        >
          {t('lms.consultation.registrationModal.register')}
        </BrgButton>
      </Actions>
    </ModalWrapper>
  );
};
import React, { FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useProfile } from '@brg/auth';
import { BrgButton, ButtonVariant, useToasts } from '@brg/ui';
import { LayoutConst, useModal } from 'core';
import {
  ConsultationScheduleOperation,
  LIMIT_CONSULTATION_SIGNUP_EXCEEDED,
  generateConsultationSlots,
  useConsultationScheduleStudent,
} from 'shared/hooks/api/LMS/useConsultationScheduleStudent';

const CONNECT_EARLY_MS = 60 * 1000;

interface ConsultationLesson {
  id?: string;
  title?: string | Array<{ lang: string; name: string }>;
  start?: string;
  end?: string;
  teacherName?: string;
  teacher?: string;
  isGuestTeacher?: boolean | null;
  lessonLink?: string | null;
  location?: string | null;
  room?: string | null;
  consultationFormat?: number | string | null;
  studentsMaxLimit?: number | null;
  timeSlotPerRerson?: number | null;
  signUpSheetIds?: Record<string, string> | null;
  consultation?: {
    capacity?: number;
    timePerPerson?: number;
    teacherName?: string;
    format?: 'online' | 'offline' | number | string;
    location?: string;
    room?: string;
    unavailableSlots?: string[];
    isInvitedTeacher?: boolean | null;
    isGuestTeacher?: boolean | null;
  };
}

export interface ConsultationRegistrationModalProps {
  lesson: ConsultationLesson;
  onSuccess?: () => void;
}

const ModalWrapper = styled.div`
  width: 720px;
  max-width: calc(100vw - 32px);
  padding: 48px;
  display: flex;
  flex-direction: column;
  gap: 48px;
  border: 1px solid #e7e7e7;
  border-radius: 8px;
  background: #ffffff;

  @media (max-width: ${LayoutConst.screen.s}px) {
    max-width: calc(100vw - 16px);
    padding: 24px 16px;
    gap: 24px;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
`;

const Title = styled.h2`
  margin: 0;
  color: #151514;
  font-family: Inter;
  font-size: 28px;
  font-weight: 600;
  line-height: 34px;
  letter-spacing: -0.56px;
`;

const Description = styled.p`
  margin: 0;
  color: #151514;
  font-family: Inter;
  font-size: 17px;
  font-weight: 400;
  line-height: 22px;
  letter-spacing: -0.34px;
`;

const HeaderDescription = styled(Description)`
  margin-top: 8px;
`;

const CloseButton = styled.button`
  width: 32px;
  height: 32px;
  padding: 0;
  border: 0;
  background: transparent;
  color: #909090;
  cursor: pointer;
  font-size: 36px;
  font-weight: 300;
  line-height: 24px;

  &:focus,
  &:focus-visible {
    outline: none;
  }
`;

const Slots = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px 16px;
`;

const EmptySlots = styled.div`
  color: #909090;
  font-family: Inter;
  font-size: 15px;
  font-weight: 400;
  line-height: 20px;
`;

const SlotButton = styled.button<{ $selected: boolean; $disabled: boolean }>`
  min-width: 112px;
  padding: 5px 16px;
  border: 1px solid ${({ $disabled }) => ($disabled ? '#b6b6b5' : '#909090')};
  border-radius: 5px;
  background: ${({ $selected }) => ($selected ? '#70706f' : '#ffffff')};
  color: ${({ $selected, $disabled }) => {
    if ($selected) return '#ffffff';
    return $disabled ? '#b6b6b5' : '#151514';
  }};
  cursor: ${({ $disabled }) => ($disabled ? 'default' : 'pointer')};
  font-family: Inter;
  font-size: 15px;
  font-weight: 400;
  line-height: 20px;

  &:focus,
  &:focus-visible {
    outline: none;
  }
`;

const RecordDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 8px;
`;

const ViewContent = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
`;

const RecordDetail = styled.div`
  color: #151514;
  font-family: Inter;
  font-size: 20px;
  font-weight: 600;
  line-height: 26px;
  letter-spacing: -0.4px;
`;

const Actions = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
  width: 100%;
`;

const getLessonTitle = (lesson: ConsultationLesson, language: string) => {
  if (typeof lesson.title === 'string') return lesson.title;

  return lesson.title?.find((item) => item.lang === language.toLowerCase())?.name
    || lesson.title?.[0]?.name
    || '';
};

const findStudentRegistrationSlot = (
  lesson: ConsultationLesson,
  accountId?: string | null,
) => {
  if (!accountId || !lesson.signUpSheetIds) return null;

  const entry = Object.entries(lesson.signUpSheetIds).find(
    ([, studentId]) => String(studentId) === String(accountId),
  );

  return entry?.[0] || null;
};

const isOfflineConsultation = (lesson: ConsultationLesson) => {
  const format = lesson.consultationFormat ?? lesson.consultation?.format;
  return format === 1 || String(format).toLowerCase() === 'offline';
};

const getConsultationLocation = (lesson: ConsultationLesson) => (
  lesson.location
  || lesson.room
  || lesson.consultation?.location
  || lesson.consultation?.room
  || lesson.lessonLink
  || ''
);

const formatSlotDateTime = (
  slotStart: string,
  durationMinutes: number,
  language: string,
) => {
  const start = new Date(slotStart);
  if (Number.isNaN(start.getTime())) return '';

  const end = new Date(start.getTime() + durationMinutes * 60 * 1000);
  const locale = language === 'en' ? 'en-US' : 'ru-RU';
  const datePart = start.toLocaleDateString(locale, {
    day: 'numeric',
    month: 'long',
  });
  const startTime = start.toLocaleTimeString(locale, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
  const endTime = end.toLocaleTimeString(locale, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });

  return `${datePart}, ${startTime}-${endTime}`;
};

export const ConsultationRegistrationModal: FC<ConsultationRegistrationModalProps> = ({
  lesson,
  onSuccess,
}) => {
  const { t, i18n } = useTranslation();
  const modal = useModal();
  const toasts = useToasts();
  const profile = useProfile();
  const { scheduleStudent, isLoading } = useConsultationScheduleStudent();
  const [selectedSlot, setSelectedSlot] = useState<string | null>(null);
  const lessonStart = useMemo(() => (lesson.start ? new Date(lesson.start) : null), [lesson.start]);
  const [now, setNow] = useState(() => Date.now());
  const slotDuration = lesson.consultation?.timePerPerson || lesson.timeSlotPerRerson || 15;
  const maxSlots = lesson.consultation?.capacity ?? lesson.studentsMaxLimit ?? null;
  const studentSlot = useMemo(
    () => findStudentRegistrationSlot(lesson, profile?.accountId),
    [lesson, profile?.accountId],
  );
  const isViewMode = Boolean(studentSlot);
  const isOffline = isOfflineConsultation(lesson);
  const location = getConsultationLocation(lesson);
  const connectAt = lessonStart ? lessonStart.getTime() - CONNECT_EARLY_MS : null;

  useEffect(() => {
    if (connectAt == null) return undefined;
    const delay = connectAt - Date.now();
    if (delay <= 0) {
      setNow(Date.now());
      return undefined;
    }
    const timeoutId = window.setTimeout(() => setNow(Date.now()), delay);
    return () => window.clearTimeout(timeoutId);
  }, [connectAt]);

  const canConnect = Boolean(
    studentSlot
    && !isOffline
    && lesson.lessonLink
    && connectAt != null
    && now >= connectAt,
  );

  const unavailableSlotTimes = useMemo(() => {
    const fromSheet = Object.keys(lesson.signUpSheetIds || {})
      .map((value) => new Date(value).getTime())
      .filter((value) => !Number.isNaN(value));
    const fromConsultation = (lesson.consultation?.unavailableSlots || [])
      .map((value) => new Date(value).getTime())
      .filter((value) => !Number.isNaN(value));

    return new Set([...fromSheet, ...fromConsultation]);
  }, [lesson.consultation?.unavailableSlots, lesson.signUpSheetIds]);

  const slots = useMemo(() => generateConsultationSlots({
    start: lesson.start,
    end: lesson.end,
    durationMinutes: slotDuration,
    maxSlots,
    referenceStart: lesson.start,
  }), [lesson.end, lesson.start, maxSlots, slotDuration]);

  const dateLabel = lessonStart?.toLocaleDateString(i18n.language === 'en' ? 'en-US' : 'ru-RU', {
    day: 'numeric',
    month: 'long',
  }) || '';
  const teacher = lesson.consultation?.teacherName || lesson.teacherName || lesson.teacher || '';
  const isInvitedTeacher = Boolean(
    lesson.isGuestTeacher
    ?? lesson.consultation?.isInvitedTeacher
    ?? lesson.consultation?.isGuestTeacher
  );
  const teacherLabel = teacher
    ? t(
      isInvitedTeacher
        ? 'lms.consultation.registrationModal.invitedTeacher'
        : 'lms.consultation.registrationModal.teacher',
      { name: teacher },
    )
    : '';
  const description = [getLessonTitle(lesson, i18n.language), teacherLabel]
    .filter(Boolean)
    .join('. ');
  const slotDateTimeLabel = studentSlot
    ? formatSlotDateTime(studentSlot, slotDuration, i18n.language)
    : '';

  const handleRegister = async () => {
    if (!lesson.id || !selectedSlot || !profile?.accountId) return;

    const result = await scheduleStudent({
      lessonId: lesson.id,
      timeSlot: selectedSlot,
      studentId: profile.accountId,
      operation: ConsultationScheduleOperation.Add,
    });

    if (!result.success) {
      toasts.error(
        result.error === LIMIT_CONSULTATION_SIGNUP_EXCEEDED
          ? t('lms.consultation.registrationModal.limitExceeded')
          : t('lms.consultation.registrationModal.error'),
      );
      return;
    }

    toasts.info(t('lms.consultation.registrationModal.success'));
    onSuccess?.();
    modal.hide();
  };

  const handleCancelRegistration = async () => {
    if (!lesson.id || !studentSlot || !profile?.accountId) return;

    const result = await scheduleStudent({
      lessonId: lesson.id,
      timeSlot: studentSlot,
      studentId: profile.accountId,
      operation: ConsultationScheduleOperation.Remove,
    });

    if (!result.success) {
      toasts.error(t('lms.consultation.registrationModal.error'));
      return;
    }

    toasts.info(t('lms.consultation.registrationModal.cancelSuccess'));
    onSuccess?.();
    modal.hide();
  };

  const handleConnect = () => {
    if (!canConnect || !lesson.lessonLink) return;
    window.open(lesson.lessonLink, '_blank', 'noopener,noreferrer');
  };

  if (isViewMode) {
    return (
      <ModalWrapper>
        <Header>
          <ViewContent>
            <Title>{t('lms.consultation.registrationModal.recordTitle')}</Title>
            <RecordDetails>
              {description && <Description>{description}</Description>}
              {slotDateTimeLabel && <RecordDetail>{slotDateTimeLabel}</RecordDetail>}
              {isOffline && location && <RecordDetail>{location}</RecordDetail>}
            </RecordDetails>
          </ViewContent>
          <CloseButton type="button" onClick={() => modal.hide()} aria-label="close">×</CloseButton>
        </Header>

        <Actions>
          <BrgButton
            fullWidth
            variant={ButtonVariant.Hollow}
            disabled={isLoading}
            onClick={handleCancelRegistration}
          >
            {t('lms.consultation.registrationModal.cancelRegistration')}
          </BrgButton>
          {isOffline ? (
            <BrgButton fullWidth variant={ButtonVariant.Hollow} onClick={() => modal.hide()}>
              {t('lms.consultation.registrationModal.ok')}
            </BrgButton>
          ) : (
            <BrgButton
              fullWidth
              variant={ButtonVariant.Filled}
              disabled={!canConnect || isLoading}
              onClick={handleConnect}
            >
              {t('lms.consultation.registrationModal.connect')}
            </BrgButton>
          )}
        </Actions>
      </ModalWrapper>
    );
  }

  return (
    <ModalWrapper>
      <Header>
        <div>
          <Title>{t('lms.consultation.registrationModal.title', { date: dateLabel })}</Title>
          {description && <HeaderDescription>{description}</HeaderDescription>}
        </div>
        <CloseButton type="button" onClick={() => modal.hide()} aria-label="close">×</CloseButton>
      </Header>

      <Slots>
        {slots.length === 0 ? (
          <EmptySlots>{t('lms.consultation.registrationModal.noSlots')}</EmptySlots>
        ) : (
          slots.map((slot) => {
            const isDisabled = unavailableSlotTimes.has(slot.time);
            return (
              <SlotButton
                key={slot.id}
                type="button"
                $selected={selectedSlot === slot.id}
                $disabled={isDisabled}
                disabled={isDisabled}
                onClick={() => setSelectedSlot(slot.id)}
              >
                {slot.label}
              </SlotButton>
            );
          })
        )}
      </Slots>

      <Actions>
        <BrgButton fullWidth variant={ButtonVariant.Hollow} onClick={() => modal.hide()}>
          {t('lms.consultation.registrationModal.close')}
        </BrgButton>
        <BrgButton
          fullWidth
          variant={ButtonVariant.Filled}
          disabled={!selectedSlot || isLoading}
          onClick={handleRegister}
        >
          {t('lms.consultation.registrationModal.register')}
        </BrgButton>
      </Actions>
    </ModalWrapper>
  );
};
